import { SplitPathIcon } from "@shared/assets/icons/SplitPathIcon";
import { ListItem } from "@shared/components/Data/List";
import { Text } from "@solomon-labs/design/components/ui";
import React from "react";

import { leadingVenueApy } from "@/pages/Venues/mocks/venueMocks";

/**
 * The "additional earnings" row at the bottom of the positions
 * breakdown list.
 *
 * DRAFT — the enticement toward `/venues`.
 *
 * Built on `ListItem` rather than a hand-rolled row so it inherits the
 * design system's row interaction verbatim: the ember accent line that
 * fills left-to-right on hover, and the background that fills
 * top-to-bottom. That treatment doubles as the affordance — the rows
 * above it are static readouts, so this being the only row that reacts
 * to the pointer is what marks it as the actionable one.
 *
 * `ListItem` draws its own borders on the same `line-neutral` token the
 * static rows use, so the list's rhythm is unbroken.
 *
 * The headline figure is the best combined rate available in the venue
 * list — pulled from `leadingVenueApy()` rather than hardcoded, so the
 * number advertised here cannot drift from the page it links to. The
 * second line breaks it into its two parts so the figure is a claim the
 * reader can check, not one they have to trust.
 */
export function VenuesGhostRow({
    baseApy,
    onOpenVenues
}: {
    /** Solomon's base rate — the constant half of the combined figure. */
    baseApy: number;
    onOpenVenues: () => void;
}): React.ReactElement {
    const additional = leadingVenueApy();
    const combined = baseApy + additional;
    return (
        <ListItem
            itemKey="venues"
            align="center"
            padding="py-2 px-2"
            onClick={onOpenVenues}
            leading={
                /* size-8 to match the protocol marks on the rows above —
                   same footprint keeps the list's left edge aligned and
                   the two marks reading as peers. */
                <SplitPathIcon className="size-8 shrink-0 text-foreground-base" />
            }
            trailing={
                /* Same weight as the balance on the position rows
                   above — `number-lg` here dwarfed the real holding and
                   fought the section total. */
                <Text
                    variant="body"
                    render={<span />}
                    className="text-foreground-base tabular-nums shrink-0"
                >
                    {combined.toFixed(1)}%
                </Text>
            }
        >
            <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                <Text
                    variant="small"
                    render={<span />}
                    className="text-foreground-base min-w-0 truncate"
                >
                    Additional Earnings in Venues
                </Text>
                <Text
                    variant="small"
                    render={<span />}
                    className="text-foreground-muted tabular-nums min-w-0 truncate"
                >
                    {/* The two figures are colour-coded to the halves of
                        the number above: info for Solomon's constant
                        base, success for the part the venue adds
                        (matching the delta treatment on the /venues rows
                        this links to). The connecting prose stays muted
                        so the two values carry the line. */}
                    {"Base ("}
                    <span className="text-info-base">
                        {`${baseApy.toFixed(1)}%`}
                    </span>
                    {") + Additional ("}
                    <span className="text-success-base">
                        {`${additional.toFixed(1)}%`}
                    </span>
                    {")"}
                </Text>
            </div>
        </ListItem>
    );
}
